import type { TrackerIdentity } from '@nimbalyst/collab-bundle/trackers-ui';
import type { TeamMember } from '../api';
import { memberDisplayName } from '../collab/orgRoster';

/**
 * Who "me" is, for the assignment-based queues.
 *
 * This is the one value that decides whether "Assigned to me" shows this
 * member's work or nothing at all, and getting it slightly wrong is invisible:
 * `is-current-user` compares the item's stored owner against the identity's
 * facets, and an identity that matches on none of them yields zero rows, which
 * on a live surface reads as "the room has not synced yet."
 *
 * The facets that matter, per `toIdentityStrings` / `matchesIdentity`, are
 * `email`, `displayName`, `gitEmail` and `gitName`. A browser tab can supply
 * the first two honestly:
 *
 *  - **email** comes from the org roster, which is the same Stytch email the
 *    desktop's `TrackerIdentityService` writes into an item when the author is
 *    signed in. This is the load-bearing one, and it is also how the Shared Docs
 *    home joins "me".
 *  - **displayName** comes from the roster too, matching desktop's
 *    `First Last` from the Stytch profile.
 *
 * The git facets are **null and stay null**. A browser has no git config, and
 * inventing one would be worse than leaving it out: a guessed `gitName` that
 * collides with a teammate's would silently claim their items. The cost of the
 * honest answer is stated rather than hidden -- an item whose owner was written
 * as a bare git name by a signed-out desktop client does not match here.
 *
 * Returns null while the roster is still in flight, so a queue renders "not yet"
 * rather than "none of these are yours."
 */
export function trackerIdentityForMember(
  members: readonly TeamMember[],
  memberId: string,
): TrackerIdentity | null {
  const self = members.find((member) => member.memberId === memberId);
  if (!self) return null;
  return {
    email: self.email || null,
    displayName: memberDisplayName(self),
    gitName: null,
    gitEmail: null,
  };
}

/**
 * The compatibility identity passed to older tracker engines. The server
 * ignores these display fields and resolves the authenticated member id from
 * its own roster; publishing the id here keeps older clients from placing an
 * email or client-asserted profile value on the socket in the meantime.
 */
export function trackerPresenceIdentity(
  _members: readonly TeamMember[],
  memberId: string,
): { displayName: string; avatarUrl: string | null } {
  return { displayName: memberId, avatarUrl: null };
}
